import React from 'react';
import { Download, Smartphone, Zap, Scissors, Calendar, Share2, Check, ChevronRight } from 'lucide-react';
import Header, { AppDownloadBadges } from '../layout/Header';
import Footer from '../layout/Footer';

const APP_STORE_URL = "https://apps.apple.com/app/id6762516648";
const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.autopostr.app";
const LOGO_URL = "https://customer-assets.emergentagent.com/job_autoposter-32/artifacts/j0h273d6_auto.png";

const FEATURES = [
  { icon: <Zap size={20} />, title: 'One-tap posting', copy: 'Publish to every connected channel at once — no copy-paste, no re-uploading.' },
  { icon: <Scissors size={20} />, title: 'Vizard bulk clips', copy: 'Paste a long-form link, let Vizard cut viral shorts, and queue them straight from your phone.' },
  { icon: <Calendar size={20} />, title: 'Smart scheduling', copy: 'Daily time slots or custom intervals — set it once, we handle the rest.' },
  { icon: <Share2 size={20} />, title: 'All your platforms', copy: 'TikTok, Instagram, Facebook, X, YouTube, LinkedIn, Threads, Pinterest — one feed in your pocket.' },
];

const PerkRow = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
      <Check size={12} className="text-emerald-400" />
    </span>
    <span className="text-white/70 text-sm">{children}</span>
  </li>
);

export default function AppPromoPage() {
  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white" data-testid="app-promo-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/3 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-blue-500/15 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-14 sm:pt-24 sm:pb-20 grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium mb-6">
              <Smartphone size={14} /> Now on iOS &amp; Android
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
              Post everywhere.<br />
              <span className="bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text text-transparent">From one app.</span>
            </h1>
            <p className="text-white/60 text-base sm:text-lg mb-7 max-w-xl">
              AutoPostr for mobile schedules reels, shorts, and feed posts across every channel you own — in seconds.
              Carry the whole studio in your pocket.
            </p>

            <div className="mb-6" data-testid="app-promo-hero-badges">
              <AppDownloadBadges />
            </div>

            <div className="flex items-center gap-5 text-white/50 text-xs">
              <div className="flex items-center gap-1.5"><span className="text-yellow-400">★ ★ ★ ★ ★</span> 4.8 rating</div>
              <div>50k+ clips scheduled</div>
            </div>
          </div>

          {/* Phone mockup */}
          <div className="relative mx-auto hidden lg:block">
            <div className="relative w-[290px] h-[590px] rounded-[48px] bg-gradient-to-b from-neutral-900 to-black border-8 border-neutral-800 shadow-[0_30px_80px_-20px_rgba(16,185,129,0.4)] overflow-hidden">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-2xl z-10" />
              <div className="h-full w-full bg-gradient-to-br from-emerald-500/20 via-black to-blue-500/20 p-5 pt-10">
                <img src={LOGO_URL} alt="" className="w-8 h-8 opacity-90 mb-4" />
                <div className="space-y-3">
                  {['TikTok', 'Instagram', 'YouTube Shorts', 'X', 'Facebook'].map((p, i) => (
                    <div key={p} className={`p-3 rounded-xl bg-white/5 border border-white/10 flex items-center gap-3 ${i === 0 ? 'ring-1 ring-emerald-400' : ''}`}>
                      <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-emerald-400 to-blue-400 flex items-center justify-center text-black text-sm font-bold">
                        {p[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium">{p}</p>
                        <p className="text-white/40 text-[11px]">Queued · 9:00 AM</p>
                      </div>
                      <Check size={14} className="text-emerald-400" />
                    </div>
                  ))}
                </div>
                <button className="mt-5 w-full py-2.5 rounded-xl bg-emerald-500 text-white text-sm font-semibold">
                  Post to all 5
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature grid */}
      <section className="max-w-6xl mx-auto px-5 py-14 sm:py-20">
        <h2 className="text-2xl sm:text-3xl font-bold mb-10 text-center">Built for creators who ship daily</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map(f => (
            <div key={f.title} data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
              className="p-5 rounded-2xl bg-[#111] border border-white/10 hover:border-emerald-500/30 transition-colors">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-400 mb-3">{f.icon}</div>
              <h3 className="font-semibold mb-1.5">{f.title}</h3>
              <p className="text-white/50 text-sm leading-relaxed">{f.copy}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Why mobile */}
      <section className="max-w-6xl mx-auto px-5 pb-14 sm:pb-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">Shoot it. Caption it. Schedule it. All on your phone.</h2>
          <p className="text-white/60 mb-6">
            The web dashboard is still there when you want it — but with the app, your batch of weekend clips never has to wait until Monday.
          </p>
          <ul className="space-y-3 mb-8">
            <PerkRow>Native video upload, no browser timeouts</PerkRow>
            <PerkRow>Sign in with Apple or Google in two taps</PerkRow>
            <PerkRow>Push notifications when a post publishes or fails</PerkRow>
            <PerkRow>Same AutoPostr Pro — one subscription, all platforms</PerkRow>
          </ul>
          <div data-testid="app-promo-secondary-badges"><AppDownloadBadges /></div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="qr-ios-card"
            className="p-6 rounded-2xl bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-emerald-500/40 transition-all">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center mb-4">
              <Smartphone size={24} className="text-black" />
            </div>
            <p className="font-semibold">iOS App</p>
            <p className="text-white/40 text-xs mb-3">Requires iOS 15.0+</p>
            <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium">
              Open App Store <ChevronRight size={12} />
            </span>
          </a>
          <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer" data-testid="qr-android-card"
            className="p-6 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-blue-500/5 border border-emerald-500/20 hover:border-emerald-500/40 transition-all">
            <div className="w-12 h-12 rounded-xl bg-emerald-500 flex items-center justify-center mb-4">
              <Download size={24} className="text-black" />
            </div>
            <p className="font-semibold">Android App</p>
            <p className="text-white/40 text-xs mb-3">Requires Android 9+</p>
            <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium">
              Open Google Play <ChevronRight size={12} />
            </span>
          </a>
        </div>
      </section>

      {/* CTA band */}
      <section className="max-w-5xl mx-auto px-5 pb-20">
        <div className="rounded-3xl p-8 sm:p-12 bg-gradient-to-br from-emerald-500/15 via-black to-blue-500/15 border border-white/10 text-center">
          <h2 className="text-2xl sm:text-4xl font-bold mb-3">Your scheduler, pocket-sized.</h2>
          <p className="text-white/60 mb-6 max-w-xl mx-auto">
            Join thousands of creators scheduling from the beach, the train, or the green room.
          </p>
          <div className="flex justify-center" data-testid="app-promo-footer-badges">
            <AppDownloadBadges />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
